import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import {
  ShopConfigAuditLogDto,
  useGetShopConfigAuditLogs,
} from "@babackapp/private-api-client/client/use-get-shop-config-audit-logs";
import { useGetShopMembers } from "@babackapp/private-api-client/client/use-get-shop-members";
import { Badge } from "@babackapp/ui/components/badge";
import { Button } from "@babackapp/ui/components/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "@babackapp/ui/components/data-table";
import { useMemo, useState } from "react";
import useTranslation from "@babackapp/translations/use-translation";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 50;

const ACTION_BADGE_VARIANT: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  UPDATE: "secondary",
  CREATE: "default",
  DELETE: "destructive",
};

const FIELD_PREFIXES = [
  "discounts",
  "refunds",
  "exchanges",
  "reasons",
  "returnToStore",
  "workflowLogistician",
  "preferences",
  "preferences.customisation",
  "preferences.notifications",
  "storeShippingSettings",
  "tracking.carrierEmailFilter",
];

// Section prefix → translation key (reuses existing settings page keys)
const SECTION_TRAD_KEYS: Record<string, string> = {
  discounts: "settings.customerPortal.sections.discounts.title",
  exchanges: "settings.customerPortal.sections.exchanges.title",
  refunds: "settings.customerPortal.sections.refunds.title",
  reasons: "settings.customerPortal.sections.returnReasons.title",
  preferences: "settings.customerPortal.sections.basics.title",
  "preferences.customisation":
    "settings.customerPortal.sections.customization.title",
  "preferences.notifications":
    "settings.customerPortal.sections.notifications.title",
  workflowLogistician: "settings.config.workflowLogistician.name",
};
// Fallback hardcoded for sections without a matching key in settings pages
const SECTION_LABELS_FALLBACK: Record<string, string> = {
  returnToStore: "Retour en magasin",
  "returnToStore.stores": "Retour en magasin",
  storeShippingSettings: "Paramètres d'expédition",
  "tracking.carrierEmailFilter": "Filtrage des emails transporteur",
  "preferences.userPortal": "Portail client",
};

// "section.fieldKey" → label, for sections whose form lives in another app and
// whose translation keys are therefore not reachable from here
const FIELD_LABELS_FALLBACK: Record<string, string> = {
  "tracking.carrierEmailFilter.enabled": "Interception activée",
  "tracking.carrierEmailFilter.allowedCategories": "Catégories transférées",
};

// "section.fieldKey" → translation key reused from settings forms
const FIELD_TRAD_KEYS: Record<string, string> = {
  // discounts
  "discounts.activated": "settings.config.discount.activated.title",
  "discounts.discountFreeShippingActivated":
    "settings.config.discount.freeShippingActivated.title",
  "discounts.freeShipping":
    "settings.config.discount.freeShippingActivated.title",
  "discounts.freeShippingCode":
    "settings.config.discount.freeShippingCode.title",
  "discounts.giftCard": "settings.config.discount.giftCard.title",
  "discounts.validity": "settings.config.discount.validity.title",
  "discounts.tagsForNotAcceptedDiscount":
    "settings.config.discount.tagsForNotAcceptedDiscount.title",
  "discounts.applyReturnShippingFeesForDiscountRule":
    "settings.config.discount.applyReturnShippingFeesForDiscountRule.title",
  "discounts.minimalDiscountForFreeShipping":
    "settings.config.discount.minimumAmountForFreeShipping.title",
  "discounts.codesToIgnoreRegex":
    "settings.config.discount.codesToIgnore.title",
  "discounts.discountAcceptedCountries":
    "settings.config.discount.discountAcceptedCountries.title",
  // exchanges
  "exchanges.activated": "settings.exchange.fields.activated.title",
  "exchanges.maxDifference": "settings.exchange.fields.maxDifference.title",
  "exchanges.reserveInventory":
    "settings.exchange.fields.reserveInventory.title",
  "exchanges.minimalStock": "settings.exchange.fields.minimalStock.title",
  "exchanges.exchangeAcceptedCountries":
    "settings.exchange.fields.exchangeAcceptedCountries.title",
  "exchanges.tagsForNewOrder": "settings.exchange.fields.tagsForNewOrder.title",
  "exchanges.tagsForNotExchangeOrder":
    "settings.exchange.fields.tagsForNotExchangeOrder.title",
  "exchanges.reshippingHasToBePaid":
    "settings.exchange.fields.reshippingHasToBePaid.title",
  "exchanges.exchangeWithOtherArticles":
    "settings.exchange.fields.exchangeWithOtherArticles.title",
  "exchanges.exchangeCreditRemainingMethod":
    "settings.exchange.fields.exchangeCreditRemainingMethod.title",
  "exchanges.exchangeOrderAt0":
    "settings.exchange.fields.exchangeOrderAt0.title",
  "exchanges.exchangeCreditAcceptedCountries":
    "settings.exchange.fields.exchangeCreditAcceptedCountries.title",
  "exchanges.tagsForNotExchangeCreditOrder":
    "settings.exchange.fields.tagsForNotExchangeCreditOrder.title",
  "exchanges.customAttributeKeysToForward":
    "settings.exchange.fields.customAttributeKeysToForward.title",
  "exchanges.exchangeWithOtherArticlesPaymentButtonQuerySelector":
    "settings.exchange.fields.exchangeWithOtherArticlesPaymentButtonQuerySelector.title",
  // refunds
  "refunds.tagsForNotAcceptedRefund":
    "settings.refund.tagsForNotAcceptedRefund.title",
  // preferences (basics)
  "preferences.archiveOrderOnCancellation":
    "settings.customerPortal.basics.fields.archiveOrderOnCancellation.title",
  "preferences.retractationEnabled":
    "settings.customerPortal.basics.fields.retractationEnabled.title",
  "preferences.languages":
    "settings.customerPortal.basics.fields.languages.title",
  "preferences.webOrderOnly":
    "settings.customerPortal.basics.fields.webOrderOnly.title",
  "preferences.tagsForNotAcceptedOrder":
    "settings.customerPortal.basics.fields.tagsForNotAcceptedOrder.title",
  "preferences.restockOnReturn":
    "settings.customerPortal.basics.fields.restockOnReturn.title",
  "preferences.contactLink":
    "settings.customerPortal.basics.fields.contactLink.title",
  "preferences.nonReturnableItems":
    "settings.customerPortal.basics.fields.nonReturnableItems.title",
  "preferences.clientCanUpdatePersonalInformation":
    "settings.customerPortal.basics.fields.clientCanUpdatePersonalInformation.title",
  "preferences.canCancelReturnRequest":
    "settings.customerPortal.basics.fields.canCancelReturnRequest.title",
  "preferences.allowOnlyUniqueReturnRequest":
    "settings.customerPortal.basics.fields.allowOnlyUniqueReturnRequest.title",
  "preferences.oneResolutionPerReturn":
    "settings.shop.oneResolutionPerReturn.title",
  "preferences.singleArticleReturnOnly":
    "settings.shop.singleArticleReturnOnly.title",
  "preferences.groupItemsByFulfillment":
    "settings.shop.groupItemsByFulfillment.title",
  "preferences.refundInitialShippingAmountRule":
    "settings.customerPortal.basics.fields.refundInitialShippingAmountRule.title",
  "preferences.taxRefundPolicy":
    "settings.customerPortal.basics.fields.taxRefundPolicy.title",
  "preferences.autoTreateOnLogisticianValidate":
    "settings.customerPortal.basics.fields.autoTreateOnLogisticianValidate.title",
  "preferences.showHomeToLogistician":
    "settings.customerPortal.basics.fields.showHomeToLogistician.title",
  "preferences.showReturnRateToLogistician":
    "settings.customerPortal.basics.fields.showReturnRateToLogistician.title",
  // returnToStore
  "returnToStore.activated": "settings.returnToStore.fields.activation.title",
  // reasons sub-fields (used when field is reasons.{id}.fieldName)
  "reasons.skipReturnCost": "settings.reasons.fields.skipReturnCost.title",
  "reasons.skipReshipping": "settings.reasons.fields.skipReshipping.title",
  "reasons.highlighted": "settings.reasons.fields.highlighted.title",
  "reasons.reservedForLoggedUser":
    "settings.reasons.fields.reservedForLoggedUser.title",
  "reasons.babackStandardReason":
    "settings.reasons.fields.shopifyEquivalence.title",
  "reasons.images": "settings.reasons.fields.imageField.title",
  "reasons.comments": "settings.reasons.fields.commentField.title",
  "reasons.externalId": "settings.reasons.fields.externalId.title",
};

function humanize(key: string): string {
  return key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());
}

function getItemLabel(before: unknown, after: unknown): string | null {
  const item = (after ?? before ?? {}) as Record<string, unknown>;
  // reasons: values[{lang, text}]
  if (Array.isArray(item.values)) {
    const vals = item.values as Array<{ lang: string; text: string }>;
    const fr = vals.find((v) => v.lang === "fr-FR") ?? vals[0];
    if (fr?.text) return fr.text;
  }
  // stores: companyName or name
  if (typeof item.companyName === "string") return item.companyName;
  if (typeof item.name === "string") return item.name;
  return null;
}

// Real IDs (MongoDB short IDs, UUIDs) always contain digits or dashes.
// camelCase field names are pure alphabetic — never digits.
function looksLikeId(key: string): boolean {
  return /[0-9]/.test(key) || key.includes("-");
}

type TFn = (key: string) => string;

function resolveSectionLabel(sectionKey: string, t: TFn): string {
  if (SECTION_TRAD_KEYS[sectionKey]) return t(SECTION_TRAD_KEYS[sectionKey]);
  return (
    SECTION_LABELS_FALLBACK[sectionKey] ??
    humanize(sectionKey.split(".").pop()!)
  );
}

function resolveFieldKeyLabel(
  sectionKey: string,
  fieldKey: string,
  t: TFn,
): string {
  const tradKey = FIELD_TRAD_KEYS[`${sectionKey}.${fieldKey}`];
  if (tradKey) return t(tradKey);
  return (
    FIELD_LABELS_FALLBACK[`${sectionKey}.${fieldKey}`] ?? humanize(fieldKey)
  );
}

function formatFieldLabel(
  field: string,
  before: unknown,
  after: unknown,
  t: TFn,
): string {
  const parts = field.split(".");

  // Find the longest matching section prefix
  let sectionKey = parts[0];
  for (let len = parts.length - 1; len >= 2; len--) {
    const candidate = parts.slice(0, len).join(".");
    if (SECTION_TRAD_KEYS[candidate] || SECTION_LABELS_FALLBACK[candidate]) {
      sectionKey = candidate;
      break;
    }
  }

  const sectionLabel = resolveSectionLabel(sectionKey, t);
  const remainder = parts.slice(sectionKey.split(".").length);

  if (remainder.length === 0) return sectionLabel;

  const [keyOrId, ...rest] = remainder;

  if (keyOrId === "order" && rest.length === 0) {
    return `${sectionLabel} — Ordre`;
  }

  // Looks like an item ID → resolve label from before/after
  if (looksLikeId(keyOrId)) {
    const label = getItemLabel(before, after);
    const suffix = label ?? `#${keyOrId.slice(0, 8)}`;
    if (rest.length > 0) {
      return `${sectionLabel} — ${suffix} — ${resolveFieldKeyLabel(sectionKey, rest.join("."), t)}`;
    }
    return `${sectionLabel} — ${suffix}`;
  }

  // Regular key
  const keyLabel = resolveFieldKeyLabel(sectionKey, keyOrId, t);
  if (rest.length > 0) {
    return `${sectionLabel} — ${keyLabel} — ${rest.join(".")}`;
  }
  return `${sectionLabel} — ${keyLabel}`;
}

function buildColumns(
  userMap: Map<string, string>,
  t: TFn,
): ColumnDef<ShopConfigAuditLogDto>[] {
  return [
    {
      id: "createdAt",
      header: "Date",
      size: 180,
      cell: ({ row }) =>
        format(new Date(row.original.createdAt), "dd MMM yyyy HH:mm:ss", {
          locale: fr,
        }),
    },
    {
      id: "field",
      header: "Champ",
      cell: ({ row }) => {
        const { field, before, after } = row.original;
        const label = formatFieldLabel(field, before, after, t);
        return (
          <div className="flex flex-col gap-0.5">
            <span className="text-sm">{label}</span>
            <code className="text-[10px] text-muted-foreground/60">
              {field}
            </code>
          </div>
        );
      },
    },
    {
      id: "action",
      header: "Action",
      size: 100,
      cell: ({ row }) => (
        <Badge variant={ACTION_BADGE_VARIANT[row.original.action] ?? "outline"}>
          {row.original.action}
        </Badge>
      ),
    },
    {
      id: "user",
      header: "Utilisateur",
      size: 220,
      cell: ({ row }) => {
        const { userId, userEmail } = row.original;
        const display =
          (userId ? userMap.get(userId) : undefined) ??
          userEmail ??
          userId ??
          "—";
        return <span className="text-sm text-muted-foreground">{display}</span>;
      },
    },
  ];
}

export default function AuditLogPage() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const [fieldPrefix, setFieldPrefix] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(0);

  const { data: logs = [], isLoading } = useGetShopConfigAuditLogs({
    shopId: shop.id,
    field: fieldPrefix,
    limit: PAGE_SIZE,
    skip: page * PAGE_SIZE,
  });

  const { data: members = [] } = useGetShopMembers(shop.id);

  const userMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const member of members) {
      if (!member.user) continue;
      const { id, firstName, lastName, email } = member.user;
      const fullName = [firstName, lastName].filter(Boolean).join(" ");
      map.set(id, fullName || email);
    }
    return map;
  }, [members]);

  const columns = useMemo(() => buildColumns(userMap, t), [userMap, t]);

  const handleFieldChange = (value: string) => {
    setFieldPrefix(value === "all" ? undefined : value);
    setPage(0);
  };

  return (
    <div className="w-full flex justify-center pt-16 px-4">
      <div className="w-full flex-1 max-w-[1100px] flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">Audit log de configuration</h1>
          <p className="text-sm text-muted-foreground">
            Historique des modifications de configuration du shop.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Select onValueChange={handleFieldChange} defaultValue="all">
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Tous les champs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les champs</SelectItem>
              {FIELD_PREFIXES.map((p) => (
                <SelectItem key={p} value={p}>
                  {resolveSectionLabel(p, t)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DataTable
          columns={columns}
          data={logs}
          isLoading={isLoading}
          renderExpandedRow={(log) => <AuditLogDiff log={log} />}
        />

        <div className="flex items-center justify-end gap-2 pb-8">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            <ChevronLeft className="w-4 h-4" />
            Précédent
          </Button>
          <span className="text-sm text-muted-foreground">Page {page + 1}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={logs.length < PAGE_SIZE}
          >
            Suivant
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

type AnyRecord = Record<string, unknown>;

function isPrimitive(v: unknown): boolean {
  return v === null || typeof v !== "object";
}

function jsonEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function computeFieldDiffs(
  before: unknown,
  after: unknown,
): Array<{ key: string; before: unknown; after: unknown }> {
  // Both primitives or one is primitive — single diff
  if (isPrimitive(before) || isPrimitive(after)) {
    return [{ key: "", before, after }];
  }
  const b = (before ?? {}) as AnyRecord;
  const a = (after ?? {}) as AnyRecord;
  const keys = new Set([...Object.keys(b), ...Object.keys(a)]);
  const diffs: Array<{ key: string; before: unknown; after: unknown }> = [];
  for (const key of keys) {
    if (!jsonEqual(b[key], a[key])) {
      diffs.push({ key, before: b[key], after: a[key] });
    }
  }
  return diffs;
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "boolean" || typeof v === "number") return String(v);
  return JSON.stringify(v, null, 2);
}

function AuditLogDiff({ log }: { log: ShopConfigAuditLogDto }) {
  const diffs = computeFieldDiffs(log.before, log.after);

  if (diffs.length === 0) {
    return (
      <div className="px-4 py-3 text-xs text-muted-foreground bg-muted/30">
        Aucun changement détecté.
      </div>
    );
  }

  // Single-value diff (primitives at top level)
  if (diffs.length === 1 && diffs[0].key === "") {
    return (
      <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 text-xs">
        <div className="flex flex-col gap-1">
          <span className="font-medium text-muted-foreground uppercase tracking-wide text-[10px]">
            Avant
          </span>
          <pre className="bg-red-50 border border-red-200 rounded p-3 overflow-auto max-h-40 whitespace-pre-wrap break-all text-red-800">
            {formatValue(diffs[0].before)}
          </pre>
        </div>
        <div className="flex flex-col gap-1">
          <span className="font-medium text-muted-foreground uppercase tracking-wide text-[10px]">
            Après
          </span>
          <pre className="bg-green-50 border border-green-200 rounded p-3 overflow-auto max-h-40 whitespace-pre-wrap break-all text-green-800">
            {formatValue(diffs[0].after)}
          </pre>
        </div>
      </div>
    );
  }

  // Object diff — one row per changed field
  return (
    <div className="p-4 bg-muted/30 text-xs">
      <table className="w-full border-collapse">
        <thead>
          <tr className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
            <th className="text-left pb-2 w-40">Champ</th>
            <th className="text-left pb-2 w-1/2 pr-3">Avant</th>
            <th className="text-left pb-2 w-1/2">Après</th>
          </tr>
        </thead>
        <tbody>
          {diffs.map(({ key, before, after }) => (
            <tr key={key} className="border-t border-muted">
              <td className="py-1.5 pr-3 align-top">
                <code className="text-[11px]">{key}</code>
              </td>
              <td className="py-1.5 pr-3 align-top">
                <pre className="bg-red-50 text-red-800 rounded px-2 py-1 whitespace-pre-wrap break-all max-h-32 overflow-auto">
                  {formatValue(before)}
                </pre>
              </td>
              <td className="py-1.5 align-top">
                <pre className="bg-green-50 text-green-800 rounded px-2 py-1 whitespace-pre-wrap break-all max-h-32 overflow-auto">
                  {formatValue(after)}
                </pre>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
